import { Toolbar } from '@mui/material';
import Box from '@mui/material/Box';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';
import DicomTagDialog from '~/components/Dialogs/DicomTagDialog';
import Sidebar, { drawerWidth } from '../../../components/Sidebar';
import { dicomViewerAtom } from '~/state/atoms/viewerAtoms';
import WorkflowSidebarContent from './WorkflowSidebar';
import WorkflowStepper from './WorkflowStepper';

const SIDEBAR_WIDTH_KEY = 'sidebar-width';
const MIN_WIDTH = 240;
const MAX_WIDTH = 500;

function readStoredWidth(): number {
    try {
        const stored = localStorage.getItem(SIDEBAR_WIDTH_KEY);
        if (stored !== null) {
            const parsed = Number(stored);
            if (
                Number.isFinite(parsed) &&
                parsed >= MIN_WIDTH &&
                parsed <= MAX_WIDTH
            ) {
                return parsed;
            }
        }
    } catch {
        // localStorage may be unavailable
    }
    return drawerWidth;
}

export default function ExplorerSidebar() {
    const [sidebarWidth, setSidebarWidth] = useState(readStoredWidth);
    const [dicomViewer, setDicomViewer] = useRecoilState(dicomViewerAtom);
    const isDragging = useRef(false);
    const dragStartX = useRef(0);
    const dragStartWidth = useRef(0);

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!isDragging.current) {
            return;
        }
        const delta = e.clientX - dragStartX.current;
        const newWidth = Math.min(
            MAX_WIDTH,
            Math.max(MIN_WIDTH, dragStartWidth.current + delta)
        );
        setSidebarWidth(newWidth);
    }, []);

    const handleMouseUp = useCallback(() => {
        if (!isDragging.current) {
            return;
        }
        isDragging.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        setSidebarWidth((w) => {
            try {
                localStorage.setItem(SIDEBAR_WIDTH_KEY, String(w));
            } catch {
                // localStorage may be unavailable
            }
            return w;
        });
    }, []);

    useEffect(() => {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [handleMouseMove, handleMouseUp]);

    const handleResizeStart = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            isDragging.current = true;
            dragStartX.current = e.clientX;
            dragStartWidth.current = sidebarWidth;
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
        },
        [sidebarWidth]
    );

    return (
        <Box display="flex" height="100%">
            <Box height="100%">
                <Toolbar variant="dense" />
                <WorkflowStepper />
            </Box>

            <Sidebar side="left" open width={sidebarWidth}>
                <Box display="flex" flexDirection="column" height="100%">
                    <Toolbar variant="dense" />
                    <Box
                        display="flex"
                        flexDirection="column"
                        flex={1}
                        minHeight={0}
                        sx={(theme) => ({
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1,
                            mb: 1,
                        })}
                    >
                        <WorkflowSidebarContent />
                    </Box>
                </Box>
            </Sidebar>

            <Box
                onMouseDown={handleResizeStart}
                sx={{
                    width: 6,
                    cursor: 'col-resize',
                    position: 'relative',
                    flexShrink: 0,
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: 2,
                        width: 2,
                        backgroundColor: 'transparent',
                        transition: 'background-color 0.15s',
                    },
                    '&:hover::after': {
                        backgroundColor: 'divider',
                    },
                }}
            />

            <DicomTagDialog
                open={dicomViewer !== null}
                onClose={() => setDicomViewer(null)}
                dataset={dicomViewer?.dataset ?? null}
                title={dicomViewer?.title ?? ''}
            />
        </Box>
    );
}
